import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchCurrencySelectOptions } from '#/api/system/currency';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import CustomerOrderSideMsg from '#/modules/crmMgmt/customerOrder/components/CustomerOrderSideMsg';
import { buildCustomerOrderColumns } from '#/modules/crmMgmt/customerOrder/data/customerOrderColumns';
import { getCustomerOrderApiErrorMessage } from '#/modules/crmMgmt/customerOrder/helper/apiError';
import {
  applyCustomerOrderDateRangeToFilters,
} from '#/modules/crmMgmt/customerOrder/helper/customerOrderDateListFilter';
import useCustomerOrderList from '#/modules/crmMgmt/customerOrder/hooks/useCustomerOrderList';
import {
  hydrateCustomerFilterLabels,
} from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import { lookupQueryKey } from '#/utils/queryKeys';

const CUSTOMER_ORDER_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  customerOrderNo: '客戶訂單號',
  customerOrderDate: '訂單日',
};

const CustomerOrderListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.CUSTOMER_ORDER_CREATE);
  const canUpdate = useCan(PERM.CUSTOMER_ORDER_UPDATE);

  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });
  const currencyOptions = currencyOptionsQuery.data;

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useCustomerOrderList();

  useEffect(() => {
    let cancelled = false;

    hydrateCustomerFilterLabels(listQuery.filters?.customerId, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.customerId,
    customerLabelCache,
  ]);

  // 起迄須同一次寫入 URL；連續兩次 setFilter 會以舊 listQuery 為底，後者蓋掉前者
  const setCustomerOrderDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyCustomerOrderDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'customerOrderDateFrom' && value === undefined) {
      setCustomerOrderDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setCustomerOrderDateRangeFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildCustomerOrderColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onCustomerOrderDateRangeChange: setCustomerOrderDateRangeFilter,
        customerLabelCache,
        currencyOptions: currencyOptions ?? [],
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setCustomerOrderDateRangeFilter,
      customerLabelCache,
      labelCacheVersion,
      currencyOptions,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.customerOrderCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <h1 className="page_title">
            <span className="border-l-5 border-primary-400 pl-2.5">客戶訂單列表</span>
          </h1>
        </div>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getCustomerOrderApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋客戶訂單號、客戶或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={CUSTOMER_ORDER_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>

          <CustomerOrderSideMsg displayData={displayData} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default CustomerOrderListPage;
